import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faUsers,
  faMicrochip,
  faMapMarkerAlt,
  faRulerCombined,
  faShieldAlt,
} from '@fortawesome/free-solid-svg-icons'
import { useAdminAuth } from '../../hooks/useAdminAuth'
import {
  useAdminDeviceStatsQuery,
  useAdminLocationStatsQuery,
  useAdminUserStatsQuery,
  useAdminUnitCategoriesStatsQuery,
} from '../../hooks/useAdminQueries'
import { useProfileContext } from '../../contexts'
import UserInviteDialog from '../../components/admin/UserInviteDialog'

interface AdminStatsCard {
  title: string
  value: string | number
  icon: typeof faUsers
  color: string
  description: string
}

export default function AdminDashboard() {
  const { user } = useAdminAuth()
  const { profile } = useProfileContext()
  const navigate = useNavigate()
  const [showInviteDialog, setShowInviteDialog] = useState(false)

  // Fetch admin statistics from API
  const { data: deviceStats, isLoading: isLoadingDeviceStats } = useAdminDeviceStatsQuery()
  const { data: locationStats, isLoading: isLoadingLocationStats } = useAdminLocationStatsQuery()
  const { data: userStats, isLoading: isLoadingUserStats } = useAdminUserStatsQuery()
  const { data: unitStats, isLoading: isLoadingUnitStats } = useAdminUnitCategoriesStatsQuery()

  // Build stats cards from API data
  const stats: AdminStatsCard[] = [
    {
      title: 'Total Users',
      value: isLoadingUserStats ? '...' : userStats?.totalUsers || 0,
      icon: faUsers,
      color: 'bg-blue-500',
      description: 'Active system users',
    },
    {
      title: 'Active Devices',
      value: isLoadingDeviceStats ? '...' : deviceStats?.totalDevices || 0,
      icon: faMicrochip,
      color: 'bg-green-500',
      description: 'IoT devices online',
    },
    {
      title: 'Locations',
      value: isLoadingLocationStats ? '...' : locationStats?.totalLocations || 0,
      icon: faMapMarkerAlt,
      color: 'bg-purple-500',
      description: 'Registered locations',
    },
    {
      title: 'Unit Categories',
      value: isLoadingUnitStats ? '...' : unitStats?.totalCategories || 0,
      icon: faRulerCombined,
      color: 'bg-orange-500',
      description: 'Measurement units',
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Welcome back, {profile?.firstName || user?.email}. Here's what's happening with your IoT system.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className={`${stat.color} rounded-md p-3`}>
                <FontAwesomeIcon icon={stat.icon} className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-xs text-gray-500">{stat.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <FontAwesomeIcon icon={faShieldAlt} className="h-5 w-5 mr-2 text-blue-500" />
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={() => setShowInviteDialog(true)}
            className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FontAwesomeIcon icon={faUsers} className="h-4 w-4 mr-2" />
            Add User
          </button>
          <button
            onClick={() => navigate('/admin/devices')}
            className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FontAwesomeIcon icon={faMicrochip} className="h-4 w-4 mr-2" />
            Register Device
          </button>
          <button
            onClick={() => navigate('/admin/groups/new')}
            className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FontAwesomeIcon icon={faMapMarkerAlt} className="h-4 w-4 mr-2" />
            Add Location
          </button>
          <button
            onClick={() => navigate('/admin/units')}
            className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FontAwesomeIcon icon={faRulerCombined} className="h-4 w-4 mr-2" />
            Manage Units
          </button>
        </div>
      </div>

      <UserInviteDialog isOpen={showInviteDialog} onClose={() => setShowInviteDialog(false)} />
    </div>
  )
}
